// pages/sections/CullClientReviewSection.js
// Cull Client Review — RELATIONSHIP-GROUP-level decision workflow.
//
// The API (/api/diCullClientReview) groups the cull feed into relationship
// groups (from the Client Groups data) and aggregates every financial metric to
// the group. Each group is one row; expand it to see its member clients and
// their individual metrics. Decisions are per group (record_key = RG anchor).
//
// Partner view: my groups (anchor's partner === me); [Cull] / [Do Not Cull].
//   Do Not Cull requires a reason category (+ free text on "Other").
//   A STANDALONE group (one client) also gets "Rescue into a relationship
//   group" — this reuses the Client Groups move_to_rg action so the client is
//   moved into the shared grouping (and folds into the target group on refetch).
// Steering view: only groups the partner marked "Do Not Cull" (+ the reason);
//   [Cull] / [Do Not Cull], no reason required.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import CullDecisionModal from './CullDecisionModal';
import GroupAnchorPicker from './groups/GroupAnchorPicker';
import { loadAnchors, getAnchors } from './groups/clientRosterCache';
import { postResolveAction, setActionPeriod } from './groups/resolveActionQueue';
import { readError, sortDoneLast } from './reviewUtil';
import './ReviewTable.css';
import './CullClientReview.css';

const PAGE_SIZE = 10;
const CODE_RE = /(\d{5}-\d{3})/;

// Client-side test-mode state. Isolated to this section via section='cull-
// client-review' on the shared test_undo_log (see api/diTestActions). Its Undo
// reverses only Cull actions; Client Groups' Undo leaves them alone.
const TEST_MODE_KEY    = 'cull.testMode';
const TEST_SESSION_KEY = 'cull.testSessionId';
const TEST_SECTION     = 'cull-client-review';
function newTestSessionId(email) {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const rand  = Math.random().toString(36).slice(2, 8);
  return `culltest-${stamp}-${(email || 'anon').split('@')[0]}-${rand}`;
}

// Every metric on this page rounds to the nearest hundredth (2 decimals).
function fmt$(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtNum(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Realization is a ratio (0.85 → "85.00%").
function fmtPct(r) {
  if (r == null) return '—';
  return (r * 100).toFixed(2) + '%';
}
// Variance is a difference of ratios, shown in percentage points.
function fmtVarPp(v) {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(2)} pp`;
}

// Client-side re-aggregation of a group from its members — used for the
// optimistic rescue fold-in so the target group's totals + realization var
// update at the click instant (before the reconciling refetch). MUST mirror
// api/shared/cullGroups.js#aggregateGroup: sums per field, realization =
// ΣBill/ΣWIP (CY & PY), CY-WIP-weighted target, variance = realization − target.
function recomputeCullGroup(group, sumFields) {
  const members = group.members || [];
  const metrics = {};
  for (const f of (sumFields || [])) {
    metrics[f] = members.reduce((a, m) => a + (Number(m?.metrics?.[f]) || 0), 0);
  }
  const realizationCY = metrics.CY_WIPAmount ? metrics.CY_BillAmount / metrics.CY_WIPAmount : null;
  const realizationPY = metrics.PY_WIPAmount ? metrics.PY_BillAmount / metrics.PY_WIPAmount : null;
  let wNum = 0, wDen = 0;
  for (const m of members) {
    const t = m?.realizationTarget;
    const w = Number(m?.metrics?.CY_WIPAmount) || 0;
    if (t != null && w) { wNum += t * w; wDen += w; }
  }
  const realizationTarget = wDen ? wNum / wDen : null;
  return {
    ...group,
    members,
    memberCount:  members.length,
    isStandalone: members.length === 1,
    metrics,
    realizationCY,
    realizationPY,
    realizationTarget,
    realizationVarCY: (realizationCY != null && realizationTarget != null) ? realizationCY - realizationTarget : null,
    realizationVarPY: (realizationPY != null && realizationTarget != null) ? realizationPY - realizationTarget : null,
  };
}

export default function CullClientReviewSection() {
  const { email, isAdmin } = useAuth();
  const { period, periodActive } = usePeriod();

  const [search,         setSearch]         = useState('');
  const [officeFilter,   setOfficeFilter]   = useState('');
  const [decisionFilter, setDecisionFilter] = useState('');   // '' | 'pending' | 'cull' | 'do-not-cull'
  const [page,           setPage]           = useState(1);
  const [expanded,       setExpanded]       = useState(new Set());  // expanded anchors
  const [modalFor,       setModalFor]       = useState(null);       // group in the reason modal
  const [rescueFor,      setRescueFor]      = useState(null);       // standalone group being rescued
  const [view,           setView]           = useState(null);       // admin/steering override: null | 'partner' | 'steering'

  // Test mode (admin-only): decisions + rescues write to the real DB but are
  // reversible via an isolated Cull undo pool.
  const [testMode, setTestMode] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(TEST_MODE_KEY) === '1'
  );
  const [testSessionId, setTestSessionId] = useState(
    () => (typeof window !== 'undefined' ? window.localStorage.getItem(TEST_SESSION_KEY) : null)
  );
  const [pendingTestCount, setPendingTestCount] = useState(0);
  const [undoBusy, setUndoBusy] = useState(false);

  function enableTestMode() {
    const sid = testSessionId || newTestSessionId(email);
    setTestSessionId(sid);
    setTestMode(true);
    window.localStorage.setItem(TEST_MODE_KEY, '1');
    window.localStorage.setItem(TEST_SESSION_KEY, sid);
  }
  function disableTestMode() {
    setTestMode(false);
    window.localStorage.setItem(TEST_MODE_KEY, '0');
  }

  const refreshPendingCount = useCallback(async () => {
    if (!testMode) { setPendingTestCount(0); return; }
    try {
      const r = await fetch(`/api/diTestActions?section=${TEST_SECTION}`, { credentials: 'include' });
      if (!r.ok) return;
      const j = await r.json();
      setPendingTestCount(j.count || 0);
    } catch { /* non-blocking */ }
  }, [testMode]);

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const qs = view ? `?view=${view}` : '';   // null → backend's natural view
    const res = await fetch(`/api/diCullClientReview/${period}${qs}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period, view]);

  // Optimistic decisions: ordered per group, never disable the buttons, and a refetch can't overwrite
  // a decision still on the wire. A rescue edits two groups at once, so it holds refetches off with a counter.
  const mutations = useRowMutations(g => g.anchor);
  const { pinned, clearPinned, reconcile: reconcileRows } = mutations;
  const rescuesInFlight = useRef(0);
  const reconcileGroups = useCallback((prev, next, opts) => {
    if (rescuesInFlight.current > 0) return prev;
    if (!prev || !Array.isArray(prev.groups) || !next || !Array.isArray(next.groups)) return next;
    const merged = reconcileRows({ rows: prev.groups }, { rows: next.groups }, opts);
    return merged.rows === next.groups ? next : { ...next, groups: merged.rows };
  }, [reconcileRows]);

  const { data, setData, loading, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [period, email, view],
      heartbeat: { sections: ['cull-client-review'], period },
      reconcile: reconcileGroups,
    });

  // Groups decided this session stay listed (e.g. under "Pending") until the user changes a filter.
  useEffect(() => { clearPinned(); }, [search, officeFilter, decisionFilter, view, clearPinned]);

  // Stamp resolve-action bodies (move_to_rg for rescue) with this period.
  useEffect(() => { setActionPeriod(period); }, [period]);

  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  async function undoAllTestActions() {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Undo ${pendingTestCount} pending Cull test action${pendingTestCount === 1 ? '' : 's'}? This restores affected records to their pre-test state.`)) {
      return;
    }
    setUndoBusy(true);
    try {
      // Scope the undo to this section only (no sessionId → reverses every Cull
      // test action for the user, across sessions).
      const r = await fetch('/api/diTestActions/undo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ section: TEST_SECTION, period }),
      });
      if (!r.ok) throw new Error(await readError(r));
      const j = await r.json();
      const newSid = newTestSessionId(email);
      setTestSessionId(newSid);
      window.localStorage.setItem(TEST_SESSION_KEY, newSid);
      setPendingTestCount(0);
      await silentRefetch();
      toast.success(`Undone ${j.undone?.length ?? 0} action${(j.undone?.length ?? 0) === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error('Undo failed: ' + e.message, { autoClose: 10000 });
    } finally {
      setUndoBusy(false);
    }
  }

  const isSteering = !!data?.isSteering;
  const role       = isSteering ? 'steering' : 'partner';
  const canSwitchView = !!data?.canSwitchView;
  const activeView    = data?.view || (isSteering ? 'steering' : 'partner');
  const reasonCategories = data?.reasonCategories || [];
  const reasonOther      = data?.reasonOther || 'Other (explain below)';
  const readOnly         = !periodActive;
  const thresholds       = data?.thresholds || { cy: -0.10, py: -0.10 };
  const canConfigure     = !!data?.canConfigure;
  const [thrSaving, setThrSaving] = useState(false);

  async function saveThresholds(cyDec, pyDec) {
    if (!Number.isFinite(cyDec) || !Number.isFinite(pyDec) || Math.abs(cyDec) > 1 || Math.abs(pyDec) > 1) {
      toast.error('Enter valid variance percentages between -100 and 100.');
      return;
    }
    setThrSaving(true);
    try {
      const r = await fetch(`/api/diCullClientReview/${period}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'set-thresholds', varThresholdCY: cyDec, varThresholdPY: pyDec }),
      });
      if (!r.ok) throw new Error(await readError(r));
      await silentRefetch();
      toast.success('Population thresholds updated.');
    } catch (e) {
      toast.error(`Failed to update thresholds: ${e.message}`, { autoClose: 10000 });
    } finally {
      setThrSaving(false);
    }
  }

  // Optimistic group patcher. (Snapshots for revert come from the RENDERED group, never from
  // inside the updater — React may run the updater later.)
  function patchGroup(anchor, updater) {
    setData(p => {
      if (!p) return p;
      return {
        ...p,
        groups: p.groups.map(g => (String(g.anchor) === String(anchor) ? updater(g) : g)),
      };
    });
  }

  async function fireDecide(recordKey, payload) {
    const res = await fetch(`/api/diCullClientReview/${period}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'decide', recordKey, role,
        testMode, sessionId: testMode ? testSessionId : null,
        ...payload,
      }),
    });
    if (!res.ok) throw new Error(await readError(res));
  }

  function decisionStamp(decision, extra = {}) {
    return { decision, decidedAt: new Date().toISOString(), decidedBy: email || 'You', ...extra };
  }

  async function decide(group, decision, reasonCategory = null, reasonText = null) {
    const anchor = group.anchor;
    if (!anchor) return;
    // The rendered group = its state before this edit (the `group` argument may be an older copy).
    const snapshot = (data?.groups || []).find(g => String(g.anchor) === String(anchor)) || group;
    patchGroup(anchor, g => {
      const decisions = {
        ...g.decisions,
        [role]: decisionStamp(decision, role === 'partner' ? { reasonCategory, reasonText } : {}),
      };
      // A new partner answer voids steering's answer to the old one (the server does the same).
      if (role === 'partner') delete decisions.steering;
      return { ...g, decisions };
    });
    try {
      // Queued per group so rapid clicks reach the server in order; the buttons stay live meanwhile.
      await mutations.run(anchor, () => fireDecide(anchor, { decision, reasonCategory, reasonText }), {
        revert: () => patchGroup(anchor, () => snapshot),
        refetch: silentRefetch,
      });
      toast.success(decision === 'cull'
        ? `${group.anchorName || anchor}: marked Cull.`
        : `${group.anchorName || anchor}: marked Do Not Cull.`);
      if (testMode) refreshPendingCount();
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { autoClose: 10000 });
      throw err;
    }
  }

  function clickCull(group) {
    if (readOnly) return;
    decide(group, 'cull').catch(() => {});
  }
  function clickDoNotCull(group) {
    if (readOnly) return;
    if (role === 'steering') { decide(group, 'do-not-cull').catch(() => {}); return; }
    setModalFor(group);
  }
  function submitModal({ reasonCategory, reasonText }) {
    if (!modalFor) return;
    const group = modalFor;
    setModalFor(null);   // close instantly — decide() applies the optimistic patch
    decide(group, 'do-not-cull', reasonCategory, reasonText).catch(() => {});   // + revert-on-fail
  }

  // Rescue is optimistic (snapshot → patch → revert on fail), matching `decide`
  // and the app-wide convention. At the CLICK INSTANT we drop the rescued
  // standalone AND fold the client into the target group — recomputing that
  // group's totals + realization variance client-side (recomputeCullGroup) — so
  // the target immediately shows the new member and its updated numbers. We then
  // fire the move and reconcile via refetch on success (or revert on failure).
  async function rescueClient(group, target) {
    const moving     = group.members?.[0] ? { ...group.members[0] } : null;
    const clientCode = moving?.code || group.anchor;
    const snapshot = data;   // the rendered state before the move
    setData(prev => {
      if (!prev) return prev;
      const sumFields = prev.sumFields || [];
      // 1) remove the rescued standalone group.
      let groups = prev.groups.filter(g => String(g.anchor) !== String(group.anchor));
      // 2) fold the client into the target group if it's currently displayed,
      //    then re-aggregate it. (If the target isn't in view, the refetch
      //    brings it in — we can't build it locally without its other members.)
      const idx = groups.findIndex(g => String(g.anchor) === String(target));
      if (idx >= 0 && moving) {
        const tg = groups[idx];
        const already = (tg.members || []).some(m => String(m.code) === String(moving.code));
        const members = already ? tg.members : [...(tg.members || []), moving];
        const recomputed = recomputeCullGroup({ ...tg, members }, sumFields);
        groups = groups.map((g, i) => (i === idx ? recomputed : g));
      }
      return { ...prev, groups };
    });
    // Auto-expand the target so the newly-folded member is visible immediately.
    setExpanded(prev => new Set(prev).add(String(target)));
    setRescueFor(null);   // close instantly — optimistic
    rescuesInFlight.current += 1;
    try {
      await postResolveAction({
        verb: 'move_to_rg', clientCode, payload: { targetRg: target },
        testMode: !!testMode, sessionId: testMode ? testSessionId : null,
        section: 'cull-client-review',
      });
      toast.success(`${clientCode} rescued into ${target}.`);
      rescuesInFlight.current -= 1;
      await silentRefetch();                 // reconcile to the authoritative grouping
      if (testMode) refreshPendingCount();
    } catch (e) {
      rescuesInFlight.current -= 1;
      if (snapshot) setData(snapshot);        // revert
      toast.error(`Couldn't rescue ${clientCode}: ${e.message}`, { autoClose: 10000 });
      silentRefetch();                        // heal anything else that changed meanwhile
    }
  }

  function toggleExpand(anchor) {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(anchor)) n.delete(anchor); else n.add(anchor);
      return n;
    });
  }

  // ── derived view state ────────────────────────────────────────────────────

  const groups = useMemo(() => data?.groups || [], [data]);

  const offices = useMemo(
    () => [...new Set(groups.map(g => g.office))].filter(Boolean).sort(),
    [groups]
  );

  const filtered = useMemo(() => {
    let out = groups;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(g =>
        (g.anchorName || '').toLowerCase().includes(s) ||
        (g.anchor || '').toLowerCase().includes(s) ||
        (g.members || []).some(m => (m.name || '').toLowerCase().includes(s) || (m.code || '').toLowerCase().includes(s))
      );
    }
    if (officeFilter) out = out.filter(g => g.office === officeFilter);
    if (decisionFilter) {
      out = out.filter(g => {
        // Groups the user just decided stay put even if that moved them out of this filter.
        if (pinned.has(String(g.anchor))) return true;
        const d = g.decisions?.[role]?.decision || 'pending';
        return decisionFilter === 'pending' ? !g.decisions?.[role] : d === decisionFilter;
      });
    }
    // Undecided groups first, decided last, each by relationship-group code (sortDoneLast copies — `out`
    // may alias data.groups when no filter is applied).
    return sortDoneLast(out, g => !!g.decisions?.[role], g => g.anchor);
  }, [groups, search, officeFilter, decisionFilter, role, pinned]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (initialLoading) return <div className="rt-loading">Loading cull candidates…</div>;
  if (error && !data) return <div className="rt-error">Error: {error}</div>;
  if (!data) return null;

  const decidedCount = groups.filter(g => !!g.decisions?.[role]).length;
  const cullCount    = groups.filter(g => g.decisions?.[role]?.decision === 'cull').length;
  const keepCount    = groups.filter(g => g.decisions?.[role]?.decision === 'do-not-cull').length;
  const pendingCount = groups.length - decidedCount;

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">Cull Client Review</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          {isSteering && <span className="cull-stage-badge">Steering Stage</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
        <div className="cull-header-right">
          {isAdmin && (
            <label className="cull-test-toggle">
              <input type="checkbox" checked={testMode}
                onChange={(e) => (e.target.checked ? enableTestMode() : disableTestMode())} />
              <span>🧪 Test mode</span>
            </label>
          )}
          {/* View toggle stacked directly above the variance filter. */}
          <div className="cull-view-filter-stack">
            {canSwitchView && (
              <div className="cull-view-toggle">
                <span className="cull-view-toggle-label">View:</span>
                <button type="button"
                  className={`cull-view-btn ${activeView === 'partner' ? 'active' : ''}`}
                  onClick={() => setView('partner')}>
                  Partner
                </button>
                <button type="button"
                  className={`cull-view-btn ${activeView === 'steering' ? 'active' : ''}`}
                  onClick={() => setView('steering')}>
                  Steering
                </button>
              </div>
            )}
            <ThresholdBar
              cy={thresholds.cy}
              py={thresholds.py}
              canConfigure={canConfigure}
              saving={thrSaving}
              onSave={saveThresholds}
            />
          </div>
        </div>
      </div>

      {testMode && (
        <div className="cull-test-banner">
          <span aria-hidden="true">🧪</span>
          <span className="cull-test-banner-text">
            <strong>Test mode is ON.</strong> Cull decisions and rescues write to the
            database and are <em>reversible</em> here (isolated from other sections).
          </span>
          <button type="button" className="cull-btn cull-btn--keep"
            disabled={undoBusy || pendingTestCount === 0}
            onClick={undoAllTestActions}>
            {undoBusy
              ? 'Undoing…'
              : pendingTestCount > 0
                ? `Undo ${pendingTestCount} action${pendingTestCount === 1 ? '' : 's'}`
                : 'No test actions yet'}
          </button>
        </div>
      )}

      {/* One toolbar row: search + picklists on the left, counter cards on the right. */}
      <div className="cull-toolbar">
        <div className="rt-filters">
          <input className="rt-filter-input" placeholder="Search group or client…"
                 value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select className="rt-filter-select" value={officeFilter}
                  onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
            <option value="">All Offices</option>
            {offices.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select className="rt-filter-select" value={decisionFilter}
                  onChange={e => { setDecisionFilter(e.target.value); setPage(1); }}>
            <option value="">All Decisions</option>
            <option value="pending">Pending</option>
            <option value="cull">Cull</option>
            <option value="do-not-cull">Do Not Cull</option>
          </select>
          {(search || officeFilter || decisionFilter) && (
            <button type="button" className="rt-filter-clear"
              onClick={() => { setSearch(''); setOfficeFilter(''); setDecisionFilter(''); setPage(1); }}>
              Clear filters
            </button>
          )}
        </div>

        <div className="rt-summary">
          <div className="rt-stat">
            <span className="rt-stat-val">{groups.length}</span>
            <span className="rt-stat-lbl">{isSteering ? 'Do-Not-Cull Queue' : 'Your Groups'}</span>
          </div>
          <div className="rt-stat rt-stat--pending">
            <span className="rt-stat-val">{pendingCount}</span>
            <span className="rt-stat-lbl">Pending</span>
          </div>
          <div className="rt-stat cull-stat--cull">
            <span className="rt-stat-val">{cullCount}</span>
            <span className="rt-stat-lbl">Cull</span>
          </div>
          <div className="rt-stat cull-stat--keep">
            <span className="rt-stat-val">{keepCount}</span>
            <span className="rt-stat-lbl">Do Not Cull</span>
          </div>
        </div>
      </div>

      <div className="rt-table-wrap cull-table-wrap">
        {loading && (
          <div className="cull-loading-overlay" role="status" aria-live="polite">
            <span className="cull-spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        )}
        <table className="rt-table cull-table">
          <thead>
            <tr>
              <th className="cull-expand-col" />
              <th>Group / Client</th>
              <th>Office</th>
              <th>Partner</th>
              <th title="WIP hours — CY & PY chips, with Q1·Q2·Q3 distribution">Hours</th>
              <th title="WIP amount — CY & PY chips, with Q1·Q2·Q3 distribution">WIP</th>
              <th title="Billed amount — CY & PY chips, with Q1·Q2·Q3 distribution">Bill</th>
              <th title="Realization = Billed / WIP — CY & PY chips">Real</th>
              <th title="Realization variance vs target (pp) — CY & PY chips; target badge">Var</th>
              {isSteering && <th>Partner Reason</th>}
              <th className="cull-actions-col">Decision</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(group => {
              const myDecision    = group.decisions?.[role]?.decision || null;
              const partnerReason = group.decisions?.partner;
              const isOpen        = expanded.has(group.anchor);
              const canExpand     = group.memberCount > 1;
              return (
                <React.Fragment key={group.anchor}>
                  <tr className={`cull-group-row ${myDecision ? 'cull-row-decided' : ''} ${isOpen && canExpand ? 'cull-group-row--open' : ''}`}>
                    <td className="cull-expand-col">
                      {canExpand && (
                        <button type="button" className="cull-expand-btn"
                          aria-expanded={isOpen}
                          aria-label={isOpen ? 'Collapse group' : 'Expand group'}
                          onClick={() => toggleExpand(group.anchor)}>
                          {isOpen ? '▾' : '▸'}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="cull-group-name">
                        <span className="cull-group-title">{group.anchorName || group.anchor}</span>
                        <code className="cull-group-code">{group.anchor}</code>
                        {group.isStandalone
                          ? <span className="cull-badge cull-badge--solo">Standalone</span>
                          : <span className="cull-badge">{group.memberCount} clients</span>}
                      </div>
                    </td>
                    <td>{group.office || '—'}</td>
                    <td>{group.partner || '—'}</td>
                    {metricCells(group)}

                    {isSteering && (
                      <td className="cull-reason-cell">
                        {partnerReason ? (
                          <>
                            <div className="cull-reason-cat">{partnerReason.reasonCategory || '—'}</div>
                            {partnerReason.reasonText && <div className="cull-reason-text">{partnerReason.reasonText}</div>}
                            {partnerReason.carriedFrom && <div className="rt-cell-muted">Partner answer carried from {partnerReason.carriedFrom}</div>}
                          </>
                        ) : <span className="rt-cell-muted">—</span>}
                      </td>
                    )}

                    <td className="cull-actions-cell">
                      <div className="cull-actions-stack">
                        {myDecision ? (
                          <DecisionPill
                            decision={myDecision}
                            canChange={!readOnly}
                            onChooseCull={() => clickCull(group)}
                            onChooseKeep={() => clickDoNotCull(group)}
                          />
                        ) : (
                          <div className="cull-action-buttons">
                            <button type="button" className="cull-btn cull-btn--cull"
                              disabled={readOnly} onClick={() => clickCull(group)}>
                              Cull
                            </button>
                            <button type="button" className="cull-btn cull-btn--keep"
                              disabled={readOnly} onClick={() => clickDoNotCull(group)}>
                              Do Not Cull
                            </button>
                          </div>
                        )}
                        {group.decisions?.[role]?.carriedFrom && (
                          <span className="rt-cell-muted" title="Carried over from an earlier period; deciding again replaces it for this period.">
                            Carried from {group.decisions[role].carriedFrom}
                          </span>
                        )}
                        {/* Rescue a standalone client into an existing relationship group. */}
                        {group.isStandalone && !isSteering && !readOnly && (
                          <button type="button" className="cull-rescue-btn"
                            onClick={() => setRescueFor(group)}
                            title="Move this standalone client into a relationship group">
                            Rescue into a group
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {isOpen && canExpand && [...(group.members || [])]
                    .sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), undefined, { numeric: true }))
                    .map(m => (
                    <tr key={`${group.anchor}:${m.code}`} className="cull-member-row">
                      <td className="cull-expand-col" />
                      <td className="cull-member-name">
                        <div className="cull-group-name">
                          <span className="cull-group-title cull-member-title">{m.name || m.code}</span>
                          <code className="cull-group-code">{m.code}</code>
                        </div>
                      </td>
                      <td>{m.office || '—'}</td>
                      <td>{m.partner || '—'}</td>
                      {metricCells(m)}
                      {isSteering && <td />}
                      <td />
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && groups.length > 0 && (
          <div className="rt-empty">No groups match.</div>
        )}
        {groups.length === 0 && (
          <div className="rt-empty">
            {isSteering ? 'No groups awaiting steering review.' : 'No groups assigned to you for cull review.'}
          </div>
        )}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="rt-pagination">
          <button disabled={safePage <= 1} onClick={() => setPage(1)}>«</button>
          <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <span className="rt-page-info">Page {safePage} of {totalPages} ({filtered.length} groups)</span>
          <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}

      <CullDecisionModal
        open={!!modalFor}
        client={modalFor ? { code: modalFor.anchor, name: modalFor.anchorName } : null}
        categories={reasonCategories}
        otherLabel={reasonOther}
        initialCategory={modalFor?.decisions?.partner?.reasonCategory || ''}
        initialText={modalFor?.decisions?.partner?.reasonText || ''}
        onSubmit={submitModal}
        onCancel={() => setModalFor(null)}
      />

      {rescueFor && (
        <RescueModal
          group={rescueFor}
          onClose={() => setRescueFor(null)}
          onSubmit={(target) => rescueClient(rescueFor, target)}
        />
      )}
    </div>
  );
}

// Population gate control. Everyone sees the active thresholds; admin/steering
// (canConfigure) can edit them. Values shown/edited as percentages; stored as
// decimals. A group appears only if BOTH its CY and PY variance are ≤ these.
// A blank box is NaN, not 0 — Number('') would quietly save a 0% threshold.
const pctToDecimal = (s) => (String(s).trim() === '' ? NaN : Number(s) / 100);

function ThresholdBar({ cy, py, canConfigure, saving, onSave }) {
  const [cyPct, setCyPct] = useState((cy * 100).toFixed(2));
  const [pyPct, setPyPct] = useState((py * 100).toFixed(2));
  useEffect(() => {
    setCyPct((cy * 100).toFixed(2));
    setPyPct((py * 100).toFixed(2));
  }, [cy, py]);

  if (!canConfigure) {
    return (
      <div className="cull-threshold-bar" role="note">
        <i className="fas fa-filter" aria-hidden="true" />
        <span>Showing groups where <strong>CY</strong> and <strong>PY</strong> realization
          variance are both ≤ <strong>{(cy * 100).toFixed(2)}%</strong> / <strong>{(py * 100).toFixed(2)}%</strong>.</span>
      </div>
    );
  }

  return (
    <div className="cull-threshold-bar">
      <i className="fas fa-filter" aria-hidden="true" />
      <span>Populate groups with</span>
      <label>CY variance ≤
        <input type="number" step="0.01" className="cull-threshold-input"
          value={cyPct} disabled={saving} onChange={(e) => setCyPct(e.target.value)} />%
      </label>
      <label>and PY variance ≤
        <input type="number" step="0.01" className="cull-threshold-input"
          value={pyPct} disabled={saving} onChange={(e) => setPyPct(e.target.value)} />%
      </label>
      <button type="button" className="cull-btn cull-btn--keep"
        disabled={saving}
        onClick={() => onSave(pctToDecimal(cyPct), pctToDecimal(pyPct))}>
        {saving ? 'Applying…' : 'Apply'}
      </button>
    </div>
  );
}

// The five metric columns (Hours, WIP, Bill, Real, Var) for a group OR a member
// row — both carry the same shape (metrics{} + realization* fields), so the
// group total and per-client rows render identically. Returns 5 <td>s.
function metricCells(o) {
  const m = o.metrics || {};
  return (
    <>
      <td><QuadCell
        cyVal={m.CY_WIPHours} cyQ={[m.CY_Q1_WIPHours, m.CY_Q2_WIPHours, m.CY_Q3_WIPHours]}
        pyVal={m.PY_WIPHours} pyQ={[m.PY_Q1_WIPHours, m.PY_Q2_WIPHours, m.PY_Q3_WIPHours]}
        fmtMain={fmtNum} fmtQ={fmtNum} /></td>
      <td><QuadCell
        cyVal={m.CY_WIPAmount} cyQ={[m.CY_Q1_WIPAmount, m.CY_Q2_WIPAmount, m.CY_Q3_WIPAmount]}
        pyVal={m.PY_WIPAmount} pyQ={[m.PY_Q1_WIPAmount, m.PY_Q2_WIPAmount, m.PY_Q3_WIPAmount]}
        fmtMain={fmt$} fmtQ={fmt$} /></td>
      <td><QuadCell
        cyVal={m.CY_BillAmount} cyQ={[m.CY_Q1_BillAmount, m.CY_Q2_BillAmount, m.CY_Q3_BillAmount]}
        pyVal={m.PY_BillAmount} pyQ={[m.PY_Q1_BillAmount, m.PY_Q2_BillAmount, m.PY_Q3_BillAmount]}
        fmtMain={fmt$} fmtQ={fmt$} /></td>
      <td><RealCell cy={o.realizationCY} py={o.realizationPY} /></td>
      <td><VarCell cy={o.realizationVarCY} py={o.realizationVarPY} target={o.realizationTarget} /></td>
    </>
  );
}

// Two chips (CY / PY) stacked, each with a segmented bar of its Q1·Q2·Q3 split.
function QuadCell({ cyVal, cyQ, pyVal, pyQ, fmtMain, fmtQ }) {
  return (
    <div className="cull-metric-cell">
      <div className="cull-chip-line">
        <span className="cull-chip cull-chip--cy">CY {fmtMain(cyVal)}</span>
        <QuadBar segs={cyQ} fmt={fmtQ} />
      </div>
      <div className="cull-chip-line">
        <span className="cull-chip cull-chip--py">PY {fmtMain(pyVal)}</span>
        <QuadBar segs={pyQ} fmt={fmtQ} />
      </div>
    </div>
  );
}

// Horizontal stacked-percentage bar of the Q1/Q2/Q3 distribution. Each segment's
// width is that quarter's share of the three-quarter total; hover for its value
// and %. Renders a neutral empty bar when there's no quarterly data.
function QuadBar({ segs, fmt }) {
  const vals  = (segs || []).map(v => (Number.isFinite(Number(v)) ? Number(v) : 0));
  const total = vals.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    return <span className="cull-quadbar cull-quadbar--empty" title="No quarterly data" aria-hidden="true" />;
  }
  const labels = ['Q1', 'Q2', 'Q3'];
  // One combined tooltip on the whole bar listing all three quarters (native
  // title honors newlines), so hovering anywhere shows the full breakdown.
  const tip = labels
    .map((l, i) => `${l}: ${fmt(vals[i])} (${((vals[i] / total) * 100).toFixed(1)}%)`)
    .join('\n');
  return (
    <span className="cull-quadbar" role="img" aria-label={tip} title={tip}>
      {vals.map((v, i) => {
        const pct = (v / total) * 100;
        if (pct <= 0) return null;
        return (
          <span
            key={i}
            className={`cull-quadbar-seg cull-quadbar-seg--q${i + 1}`}
            style={{ width: `${pct}%` }}
          />
        );
      })}
    </span>
  );
}

// Realization: CY / PY chips (no quarterly split — it's a ratio).
function RealCell({ cy, py }) {
  return (
    <div className="cull-metric-cell">
      <span className="cull-chip cull-chip--cy">CY {fmtPct(cy)}</span>
      <span className="cull-chip cull-chip--py">PY {fmtPct(py)}</span>
    </div>
  );
}

// Variance vs target: CY / PY chips (red when below target) + a target badge.
function VarCell({ cy, py, target }) {
  const cls = (v) => (v == null ? '' : (v < 0 ? 'cull-chip--neg' : 'cull-chip--pos'));
  return (
    <div className="cull-metric-cell">
      <span className={`cull-chip ${cls(cy)}`}>CY {fmtVarPp(cy)}</span>
      <span className={`cull-chip ${cls(py)}`}>PY {fmtVarPp(py)}</span>
      {target != null && <span className="cull-target-badge">Target {fmtPct(target)}</span>}
    </div>
  );
}

function DecisionPill({ decision, canChange, onChooseCull, onChooseKeep }) {
  const isCull = decision === 'cull';
  return (
    <div className="cull-decision-pill-wrap">
      <span className={`cull-decision-pill ${isCull ? 'cull-decision-pill--cull' : 'cull-decision-pill--keep'}`}>
        <i className={`fas ${isCull ? 'fa-times-circle' : 'fa-check-circle'}`} />
        {isCull ? 'Culled' : 'Do Not Cull'}
      </span>
      {canChange && (
        <button type="button" className="cull-decision-change"
          onClick={isCull ? onChooseKeep : onChooseCull}
          title={isCull ? 'Change to Do Not Cull' : 'Change to Cull'}>
          {isCull ? 'Change to Do Not Cull' : 'Change to Cull'}
        </button>
      )}
    </div>
  );
}

// Rescue a standalone client into an existing relationship group. Pure picker:
// validates the pick and hands the target to onSubmit — the parent applies the
// move OPTIMISTICALLY (drops the standalone, reverts on failure) and closes
// this modal, mirroring the decide/CullDecisionModal split.
function RescueModal({ group, onClose, onSubmit }) {
  const combine = (a) => [ ...((a && a.rgAnchors) || []), ...((a && a.rgOrphans) || []) ];
  const [anchors, setAnchors]     = useState(() => combine(getAnchors()));
  const [preferred, setPreferred] = useState([]);   // caller's own RGs (quick-picks)
  const [pick, setPick]           = useState('');
  const [error, setError]         = useState(null);

  const clientCode   = group.members?.[0]?.code || group.anchor;
  const selectedCode = (String(pick || '').match(CODE_RE) || [])[1] || null;

  useEffect(() => {
    let cancelled = false;
    loadAnchors().then(a => { if (!cancelled) setAnchors(combine(a)); }).catch(() => {});
    // "Your relationship groups" quick-picks — the caller's own RGs (the ~95%
    // case), same source the Client Groups pickers use (/api/dgMyRgAnchors).
    fetch('/api/dgMyRgAnchors', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { anchors: [] }))
      .then(j => { if (!cancelled) setPreferred((j.anchors || []).filter(a => a.clientCode !== clientCode)); })
      .catch(() => { /* fall back to search-only */ });
    return () => { cancelled = true; };
  }, [clientCode]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function submit() {
    const m = (pick || '').match(CODE_RE);
    if (!m) { setError('Pick a target relationship group.'); return; }
    const target = m[1];
    if (target === clientCode) { setError('Pick a different group than this client.'); return; }
    onSubmit(target);   // parent applies the optimistic move + closes this modal
  }

  return (
    <div className="cg-resolve-overlay" role="dialog" aria-modal="true"
      onClick={onClose}>
      <div className="cg-resolve-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cg-resolve-header">
          <h3>Rescue {group.anchorName || clientCode} into a relationship group</h3>
          <button type="button" className="cg-resolve-close"
            onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cg-resolve-confirm">
          <p className="cg-resolve-confirm-body">
            Move <code>{clientCode}</code> into an existing relationship group. This
            updates the shared Client Groups data; the client will fold into that
            group here on the next refresh.
          </p>
          {preferred.length > 0 && (
            <div className="cull-rescue-quickpick">
              <span className="cull-rescue-quickpick-label">Your relationship groups</span>
              <div className="cull-rescue-quickpick-list">
                {preferred.map(a => {
                  const label = a.clientName ? `${a.clientCode} — ${a.clientName}` : a.clientCode;
                  const isSel = selectedCode && a.clientCode === selectedCode;
                  return (
                    <button
                      type="button"
                      key={a.clientCode}
                      className={`cull-rescue-quickpick-btn ${isSel ? 'cull-rescue-quickpick-btn--sel' : ''}`}
                      onClick={() => setPick(label)}
                      title={label}
                    >
                      <code>{a.clientCode}</code>
                      {a.clientName && <span className="cull-rescue-quickpick-name"> {a.clientName}</span>}
                    </button>
                  );
                })}
              </div>
              <span className="cull-rescue-quickpick-or">Not one of yours? Search all groups below.</span>
            </div>
          )}
          <label className="cull-rescue-label">
            Relationship group anchored by
            <GroupAnchorPicker
              anchors={anchors}
              value={pick}
              onChange={setPick}
              placeholder="Start typing a group code or name…"
            />
          </label>
          {error && <div className="cg-resolve-error" role="alert"><strong>Couldn't rescue:</strong> {error}</div>}
          <div className="cg-resolve-actions">
            <button type="button" className="cg-btn cg-btn--secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="cg-btn cg-btn--primary" onClick={submit}>Rescue</button>
          </div>
        </div>
      </div>
    </div>
  );
}
